import { Row, Typography, Upload, type UploadProps, message } from 'antd';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CompanyTheme } from '../../../../api/types';
import { ErrorContent } from '../../../../components/importExport';
import CancelButton from '../../../../components/modal/CancelButton';
import OkButton from '../../../../components/modal/OkButton';
import { useThemeComponents } from '../../../../themes';
import { getDataFromExcel } from '../../../../utilities/excel';
import { allowedExtensions, validateExtensions } from '../../../../utilities/extensions';
import { getDataFromMatrixExcel } from '../../../../utilities/newVersionExcel';

const StyledUpload = styled(Upload)`
    margin-left: 10px;
`;

export type ImportFinanceTablesModalProps = {
    tableType?: 'default' | 'matrix';
    title: string;
    mappedHeaders: Record<string, string> | { key: string; text: string }[];
    open: boolean;
    onImport: (data) => void;
    onClose: () => void;
};

const ImportFinanceTablesModal = ({
    tableType = 'default',
    title,
    mappedHeaders,
    open,
    onImport,
    onClose,
}: ImportFinanceTablesModalProps) => {
    const { t } = useTranslation(['financeProductDetails', 'importExportExcel']);
    const [errors, setErrors] = useState<string>(null);
    const { theme, Modal } = useThemeComponents();

    const accept = useMemo(() => allowedExtensions.excel.join(','), []);

    const handleOnClose = useCallback(() => {
        onClose();
        setErrors(null);
    }, [onClose]);

    const handleOnChange = useCallback(
        async ({ file }) => {
            if (file instanceof File) {
                const blob = new Blob([file], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
                });
                const buffer = await blob.arrayBuffer();

                const { data, errors } = await (() => {
                    switch (tableType) {
                        case 'matrix':
                            return getDataFromMatrixExcel({
                                excel: buffer,
                                mappedHeaders: mappedHeaders as { key: string; text: string }[],
                            });
                        case 'default':
                            return getDataFromExcel({
                                excel: buffer,
                                mappedHeaders: mappedHeaders as Record<string, string>,
                            });
                        default:
                            throw new Error('Table type is not supported!');
                    }
                })();

                const errorList: string[] = [];

                // check if there is any error
                if (errors?.length > 0) {
                    errorList.push(...errors);
                }

                if (errorList.length) {
                    setErrors(errorList.join('\n'));

                    return;
                }

                onImport(data);
                onClose();
            }
        },
        [mappedHeaders, onClose, onImport]
    );

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );

    const content = useMemo(() => {
        if (!isNil(errors)) {
            return <ErrorContent errors={errors} />;
        }

        return (
            <Row align="middle" gutter={[14, 14]} justify="center">
                <Typography>{t('importExportExcel:description.message')}</Typography>
                <Typography>{t('importExportExcel:description.note')}</Typography>
            </Row>
        );
    }, [errors, t]);

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="close" onClick={handleOnClose}>
                {t('financeProductDetails:actions.close')}
            </CancelButton>
        );

        if (!isNil(errors)) {
            return [cancelButton];
        }

        const okButton = (
            <StyledUpload
                key="upload"
                accept={accept}
                beforeUpload={beforeUpload}
                onChange={handleOnChange}
                showUploadList={false}
                style={{ marginLeft: '1rem' }}
            >
                <OkButton>{t('financeProductDetails:actions.uploadFile')}</OkButton>
            </StyledUpload>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [accept, beforeUpload, errors, handleOnChange, handleOnClose, theme, t]);

    return (
        <Modal footer={footer} onCancel={handleOnClose} open={open} title={title} width={550}>
            {content}
        </Modal>
    );
};

export default ImportFinanceTablesModal;

export type ImportExcelProps = Pick<
    ImportFinanceTablesModalProps,
    'title' | 'mappedHeaders' | 'onImport' | 'tableType'
>;

export const useImportFinanceTablesModal = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (props: ImportExcelProps) => (
            <ImportFinanceTablesModal onClose={actions.close} open={visible} {...props} />
        ),
    };
};
